import { z } from "zod";
import { Amount, CostMonth } from "./amounts.js";
import { BudgetCheck, BudgetUse, type BudgetRule } from "./budgets.js";
import { IpFamily } from "./public-ips.js";
import { VolumeSizeGb } from "./volumes.js";

/*
 * Costs (docs/costs.md): our estimate from the price list and our copy of
 * the resources — the provider has no billing API. Every amount is net of
 * VAT, in the price list's currency, accrued per started hour up to the
 * monthly price, as the provider bills.
 */

/**
 * What a ledger line is for: a server's hours, its backups (a share of the
 * server price), its traffic beyond the included amount, a primary IPv4, a
 * floating IP, a volume, a snapshot, a load balancer. A new billed kind adds
 * its biller to `BILLERS` in `apps/api/src/costs/billing.ts`.
 */
export const CostKind = z.enum([
  "server",
  "backup",
  "traffic",
  "public_ip",
  "floating_ip",
  "volume",
  "snapshot",
  "load_balancer",
]);
export type CostKind = z.infer<typeof CostKind>;

export const CostByKind = z.object({ kind: CostKind, amount: Amount });
export type CostByKind = z.infer<typeof CostByKind>;

/** What a day (UTC) cost. */
export const CostDay = z.object({ date: z.iso.date(), amount: Amount });
export type CostDay = z.infer<typeof CostDay>;

export const CostMonthTotal = z.object({ month: CostMonth, amount: Amount });
export type CostMonthTotal = z.infer<typeof CostMonthTotal>;

/** One billed thing in a month; it stays in the history after it was deleted. */
export const CostResource = z.object({
  kind: CostKind,
  /** The provider's id: kept after the resource is gone. */
  providerId: z.string(),
  /** Our id while the resource exists; null once it was deleted. */
  resourceId: z.uuid().nullable(),
  name: z.string(),
  amount: Amount,
  deleted: z.boolean(),
  projectId: z.uuid(),
  projectName: z.string(),
  /**
   * The package instance it was created for (a managed appliance,
   * docs/packages.md "Resources"); null for the customer's own resources.
   */
  packageInstanceId: z.uuid().nullable(),
  packageId: z.string().nullable(),
});
export type CostResource = z.infer<typeof CostResource>;

const Summary = z.object({
  month: CostMonth,
  currency: z.string().length(3),
  /** What the month cost so far (the whole month for past months). */
  monthToDate: Amount,
  /** Month to date plus what exists now until the month ends; null for past months. */
  forecast: Amount.nullable(),
  /** The month before `month`. */
  lastMonth: Amount,
  /** The last hourly run of the meter that this view includes; null before the first. */
  meteredAt: z.iso.datetime().nullable(),
  byKind: z.array(CostByKind),
  /** Every day of the month up to today (or to its end), oldest first. */
  days: z.array(CostDay),
  /** The ten most expensive things of the month. */
  topResources: z.array(CostResource),
  /** Totals of the last 24 months, oldest first. */
  months: z.array(CostMonthTotal),
});

/** `GET /projects/:projectId/costs` — the project view (owners and guests too). */
export const ProjectCosts = Summary.extend({
  projectId: z.uuid(),
  projectName: z.string(),
  /** Everything billed in the month, most expensive first. */
  resources: z.array(CostResource),
  /** The month's budget and its use; null without a budget (docs/budgets.md). */
  budget: BudgetUse.nullable(),
});
export type ProjectCosts = z.infer<typeof ProjectCosts>;

export const OrgProjectCost = z.object({
  projectId: z.uuid(),
  projectName: z.string(),
  monthToDate: Amount,
  forecast: Amount.nullable(),
  budget: BudgetUse.nullable(),
});
export type OrgProjectCost = z.infer<typeof OrgProjectCost>;

/** `GET /costs` — the organisation view (administrators). */
export const OrgCosts = Summary.extend({ projects: z.array(OrgProjectCost) });
export type OrgCosts = z.infer<typeof OrgCosts>;

export const CostQuery = z.object({ month: CostMonth.optional() });
export type CostQuery = z.infer<typeof CostQuery>;

/**
 * A change whose cost is shown before it is made (`POST …/costs/preview`)
 * and that the budget guard checks: what it adds to the month. Locations
 * come from the server when `serverId` is given.
 */
export const CostChange = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("server.create"),
    serverType: z.string().min(1).max(64),
    region: z.string().min(1).max(64),
    count: z.number().int().min(1).max(10).default(1),
    publicIpv4: z.boolean().default(true),
    backups: z.boolean().default(false),
  }),
  z.object({
    action: z.literal("server.change_type"),
    serverId: z.uuid(),
    serverType: z.string().min(1).max(64),
  }),
  z.object({ action: z.literal("server.enable_backups"), serverId: z.uuid() }),
  z.object({ action: z.literal("server.create_snapshot"), serverId: z.uuid() }),
  z.object({
    action: z.literal("volume.create"),
    sizeGb: VolumeSizeGb,
    region: z.string().min(1).max(64).optional(),
    serverId: z.uuid().optional(),
  }),
  z.object({ action: z.literal("volume.resize"), volumeId: z.uuid(), sizeGb: VolumeSizeGb }),
  z.object({
    action: z.literal("public_ip.create"),
    family: IpFamily,
    region: z.string().min(1).max(64).optional(),
    serverId: z.uuid().optional(),
  }),
  z.object({
    action: z.literal("floating_ip.create"),
    family: IpFamily,
    region: z.string().min(1).max(64).optional(),
    serverId: z.uuid().optional(),
  }),
  /** Keeping a backup as a snapshot: a new paid service (docs/budgets.md). */
  z.object({ action: z.literal("image.convert"), imageId: z.uuid() }),
  z.object({
    action: z.literal("load_balancer.create"),
    loadBalancerType: z.string().min(1).max(32),
    region: z.string().min(1).max(64),
  }),
  z.object({
    action: z.literal("load_balancer.change_type"),
    loadBalancerId: z.uuid(),
    loadBalancerType: z.string().min(1).max(32),
  }),
  /**
   * Installing a package (docs/packages.md): its monthly price in EUR from the
   * manifest's billing for these inputs (0 while packages have no prices).
   */
  z.object({
    action: z.literal("package.install"),
    packageId: z.string().regex(/^[a-z][a-z0-9_]{1,31}$/),
    monthly: z.number().min(0).max(100_000),
    /**
     * The servers the package creates as managed appliances (docs/packages.md
     * "Resources"), priced from the catalogue like `server.create`.
     */
    servers: z
      .array(
        z.object({
          serverType: z.string().min(1).max(64),
          region: z.string().min(1).max(64),
          publicIpv4: z.boolean().default(true),
        }),
      )
      .max(20)
      .default([]),
  }),
]);
export type CostChange = z.input<typeof CostChange>;

const NEW_SERVICES = new Set<CostChange["action"]>([
  "package.install",
  "server.create",
  "server.create_snapshot",
  "volume.create",
  "floating_ip.create",
  "image.convert",
  "load_balancer.create",
]);

/**
 * Which budget rule a change falls under (docs/budgets.md): ordering a new
 * paid service, or changing one that exists. A primary IP created on an
 * existing server is "add IPv4 to a server" — a change; on its own, a new service.
 */
export function budgetRule(change: CostChange): BudgetRule {
  if (change.action === "public_ip.create") return change.serverId ? "change" : "new";
  return NEW_SERVICES.has(change.action) ? "new" : "change";
}

/** What a change costs: per month at full price, and what it adds until this month ends. */
export const CostPreview = z.object({
  currency: z.string().length(3),
  monthly: Amount,
  toMonthEnd: Amount,
  /** The project's forecast for this month without and with the change. */
  forecast: Amount,
  forecastAfter: Amount,
  /** Part of the change has no price in the catalogue (counted as zero). */
  unpriced: z.boolean(),
  /** The project's budget against the forecast after the change; null without a budget. */
  budget: BudgetCheck.nullable(),
});
export type CostPreview = z.infer<typeof CostPreview>;
